import React, { useState, useRef, useEffect, createContext, memo } from "react";
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Text,
  Keyboard,
  Platform,
  Image,
  Modal,
  Animated,
  Dimensions,
} from "react-native";
import AntDesign from "react-native-vector-icons/AntDesign";
import FontAwesome from "react-native-vector-icons/FontAwesome";
import CustomText from "./CustomText";
import CustomTextInput from "./CustomTextInput";
import { WebView } from "react-native-webview";
import { REDIRECT_URL } from "@env";
import { web, android, ios } from "./Platform";
import ArrowSpinner from "./ArrowSpinner";
import Entypo from "react-native-vector-icons/Entypo";
import EvilIcons from "react-native-vector-icons/EvilIcons";
import { getQueryString } from "../accessories/CommonFunctions.js";
import { KeyboardAvoidingView } from "react-native";
import useKeyboardHeight from "./KeyboardHeight";
import ScrollContainer from "./ScrollContainer";

const Separator = () => <View style={styles.separator} />;
//======================================================
const Option = ({
  onPress,
  text,
  value,
  style = {},
  autoFocus = 0,
  type = null,
  textStyle = {},
  iconSize = 21,
}) => {
  const ref = useRef();
  useEffect(() => {
    if (autoFocus) ref?.current?.focus();
  }, [text]);

  return (
    <>
      {web ? (
        <div
          ref={ref}
          tabIndex={0}
          onClick={onPress}
          style={{
            ...style,
            ...{
              display: "inline-flex",
              border: "2px solid #043888",
              borderRadius: 20,
              padding: "6px 12px",
              marginBottom: web ? 25 : 20,
              flexDirection: "row",
              ...(type
                ? {
                    backgroundColor: type == "primary" ? "#508BC9" : "#fff",
                    border:
                      type == "primary"
                        ? "1px solid #fff"
                        : "1px solid #508BC9",
                  }
                : {}),
            },
          }}
          onKeyDown={(event) => {
            let { keyCode } = event;
            if (keyCode === 32 || keyCode === 13) {
              event.preventDefault();
              onPress();
            }
          }}
        >
          <CustomText
            style={[
              styles.optionText,
              { fontSize: style.fontSize || 18, cursor: "pointer" },
              type
                ? {
                    color: type == "primary" ? "#fff" : "#508BC9",
                  }
                : {},
            ]}
          >
            {text}
          </CustomText>

          <CustomText
            style={{
              width: 21,
              display: "flex",
              alignSelf: "center",
              marginLeft: -5,
              cursor: "pointer",
            }}
          >
            {value && (
              <FontAwesome
                name="check"
                size={21}
                color={type == "primary" ? "#fff" : "green"}
              />
            )}
          </CustomText>
        </div>
      ) : (
        <TouchableOpacity
          activeOpacity={1}
          onPress={onPress}
          style={[
            styles.optionContainer,
            style,
            { flexDirection: "row" },
            type
              ? {
                  backgroundColor: type == "primary" ? "#508BC9" : "#fff",
                  borderColor: type == "primary" ? "#fff" : "#508BC9",
                  borderWidth: 1,
                }
              : {},
          ]}
        >
          <CustomText
            style={[
              styles.optionText,
              { fontSize: style.fontSize || 18 },
              type
                ? {
                    color: type == "primary" ? "#fff" : "#508BC9",
                  }
                : {},
              textStyle,
            ]}
          >
            {text}
          </CustomText>

          <CustomText
            style={{
              width: 21,
              display: "flex",
              alignSelf: "center",
              marginLeft: -5,
            }}
          >
            {value && (
              <FontAwesome
                name="check"
                size={iconSize}
                color={type == "primary" ? "#fff" : "green"}
              />
            )}
          </CustomText>
        </TouchableOpacity>
      )}
    </>
  );
};
//======================================================
const Button = ({
  onPress,
  title,
  style = {},
  disabled = false,
  buttonType = "",
}) => {
  return (
    <>
      {web ? (
        <div
          tabIndex={0}
          onClick={onPress}
          style={{
            ...style,
            ...{
              opacity: disabled ? 0.7 : 1,
              backgroundColor: disabled ? "#6c757d" : "#295b9a",
              borderRadius: 8,
              padding: "10px 20px",
              cursor: "pointer",
            },
          }}
          onKeyDown={(event) => {
            let { keyCode } = event;
            if (keyCode === 32 || keyCode === 13) {
              event.preventDefault();
              onPress();
            }
          }}
        >
          <CustomText
            style={[
              styles.buttonText,
              { flex: 1, alignSelf: "center", cursor: "pointer" },
            ]}
          >
            <CustomText
              style={{ color: "#fff", display: "flex", cursor: "pointer" }}
            >
              {title}
            </CustomText>
          </CustomText>
        </div>
      ) : (
        <TouchableOpacity
          activeOpacity={disabled ? 0.7 : 0.5}
          onPress={onPress}
          style={[
            styles.buttonContainer,
            style,
            { opacity: disabled ? 0.7 : 1, borderRadius: 8 },
            {
              backgroundColor: buttonType == "secondary" ? "#fff" : "#295b9a",
            },
          ]}
        >
          <CustomText style={styles.buttonText}>
            <CustomText
              style={{
                color: buttonType == "secondary" ? "#295b9a" : "#fff",
              }}
            >
              {title}
            </CustomText>
          </CustomText>
        </TouchableOpacity>
      )}
    </>
  );
};

const ModalButton = ({
  onPress,
  bold = true,
  title,
  style = {},
  textStyle = {},
  disabled = false,
  disableBackgroundColor = "#6c757d",
  icon = null,
  buttonType = "",
}) => (
  <TouchableOpacity
    activeOpacity={disabled ? 0.8 : 1}
    onPress={() => !disabled && onPress()}
    style={[
      styles.buttonContainer,
      style,
      disabled && { backgroundColor: disableBackgroundColor, opacity: 0.8 },
      disabled && web && { cursor: "not-allowed" },
    ]}
  >
    <CustomText
      style={{
        ...{ color: buttonType == "secondary" ? "#508BC9" : "#fff" },
        ...textStyle,
      }}
      bold={bold}
    >
      {icon && icon}
      {title}
    </CustomText>
  </TouchableOpacity>
);

//======================================================
const InputBox = (props) => {
  const {
    label,
    onChangeText,
    value,
    keyboardType = "default",
    placeholder,
    ref,
    secureTextEntry,
    validate = false,
    border,
    onKeyPress = () => {},
    disabled = false,
    width = "100%",
    forceValidation = false,
    formatZero = false,
  } = props;

  return (
    <View
      pointerEvents={disabled ? "none" : "auto"}
      style={[
        styles.inputBoxContainer,
        {
          width,
          borderColor: border
            ? border
            : (validate &&
                ["", 0, null, undefined, "0", "$0.00", "0.00"].includes(
                  value
                )) ||
              forceValidation
            ? "red"
            : "silver",
          marginBottom: label === "Employed From" ? 25 : 20,
        },
      ]}
    >
      <CustomText bold={true} style={styles.inputBoxLabel}>
        {label || ""}
      </CustomText>
      <CustomTextInput
        onChangeText={(text) => onChangeText(text)}
        secureTextEntry={secureTextEntry || false}
        value={value || ""}
        placeholder={placeholder || ""}
        style={styles.inputBox}
        autoCapitalize={"none"}
        ref={ref || null}
        {...props}
        onKeyPress={onKeyPress}
        keyboardType={keyboardType || "default"}
      />
    </View>
  );
};
//======================================================
const Title = ({ text, style = {} }) => (
  <>
    {text && (
      <CustomText bold={true} style={[styles.title, style]}>
        {text}
      </CustomText>
    )}
  </>
);
//======================================================
const LoanId = ({ Id, Show }) => (
  <CustomText
    bold={true}
    style={Show === true ? styles.ShowLoanId : styles.HideLoanId}
  >
    {`Loan: ${Id}`}
  </CustomText>
);
//======================================================
const IconButton = (props) => {
  let {
    onPress = () => {},
    title,
    style = {},
    textStyle = {},
    disable = false,
    autoFocus = false,
    buttonType = "PrimaryColor",
    loading = false,
    sStyle = {},
  } = props;
  const [isHovered, setIsHovered] = useState(false),
    touchableRef = useRef(null);

  useEffect(() => {
    if (autoFocus) touchableRef.current.focus();
  }, []);

  if (web) {
    useEffect(() => {
      const handleKeyPress = (event) => {
        if (event.nativeEvent.key === " ") {
          onPress();
        }
      };
      try {
        document?.removeEventListener("keydown", handleKeyPress);
        document?.addEventListener("keydown", handleKeyPress);
        return () => {
          document?.removeEventListener("keydown", handleKeyPress);
        };
      } catch (error) {}
    }, [props]);
  }

  return (
    <TouchableOpacity
      activeOpacity={1}
      onMouseEnter={() => {
        setIsHovered(true);
      }}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsHovered(true)}
      onBlur={() => setIsHovered(false)}
      onPress={(e) => {
        Keyboard.dismiss();
        if (!disable) onPress(e);
      }}
      ref={touchableRef}
      style={[
        styles.iconButtonContainer,
        disable && { opacity: 0.8 },
        style || {},
        {
          backgroundColor: buttonType == "secondary" ? "#fff" : "#508BC9",
        },
        isHovered && buttonType != "secondary" && styles.iconButtonHovered,
        isHovered && buttonType == "secondary" && styles.iconButtonHovered1,
      ]}
    >
      <CustomText style={styles.iconButtonText}>
        <View style={[web ? { display: "flex" } : { flex: 1 }]}>
          {loading ? (
            <View style={sStyle}>
              <ArrowSpinner
                duration={1500}
                isSpinner
                size={15}
                color="#fff"
                style={{
                  flex: 1,
                  margin: 5,
                  // top: !web ? 4 : 0,
                }}
              />
            </View>
          ) : (
            <CustomText
              style={{
                color: buttonType == "secondary" ? "#508BC9" : "#fff",
                fontSize:
                  getQueryString("Mobile", "") == "true" || ios ? 13 : 15,
                ...{ paddingTop: web ? 0 : 2 },
              }}
              bold={true}
            >
              {title}
            </CustomText>
          )}
        </View>
      </CustomText>
    </TouchableOpacity>
  );
};
//======================================================
const WebPageView = (props) => {
  let {
    url,
    onLoad = () => {},
    onMessage: onMessageProp = () => {},
    injectedJavaScript = "",
  } = props;
  const webViewRef = useRef(null);

  if (web && !__DEV__) {
    url = url
      .replace("https://www.directcorp.com/", "../../../")
      .replace("https://www.solutioncenter.biz/", "../../../")
      .replace("http://www.solutioncenter.biz/", "../../../");
  } else {
    url = url.replace("../../../", REDIRECT_URL);
  }

  if (web) {
    return (
      <iframe
        id="webIframe"
        src={url}
        style={{
          ...{ width: "100%", border: "none" },
          ...(props?.["style"] || {}),
        }}
        onLoad={onLoad}
      />
    );
  }
  const [pageHeight, setPageHeight] = useState(Dimensions.get("window").height);
  const onMessage = (event) => {
    const message = event.nativeEvent.data;
    const parsedData = JSON.parse(message);

    if (parsedData.type === "error") {
      console.log("Error from WebPageView ===> ", parsedData.error);
    }
    if (parsedData && parsedData.height) {
      setPageHeight(parsedData.height); // Set the height state
    }
    onMessageProp(event);
  };

  return (
    <View
      style={[
        {
          flex: 1,
          height: pageHeight,
        },
      ]}
      ref={webViewRef}
    >
      <WebView
        javaScriptEnabled={true}
        source={{ uri: url }}
        style={{ flex: 1 }}
        injectedJavaScript={injectedJavaScript}
        onMessage={onMessage}
      />
    </View>
  );
};
const DMCImage = () => {
  return (
    <View
      style={{
        flexDirection: "row",
        maxWidth: "70%",
      }}
    >
      <Image
        style={{
          position: "relative",
          height: 80,
          width: "20%",
        }}
        resizeMode="contain"
        source={require("../../assets/DW.png")}
      />
      <Image
        style={{
          position: "relative",
          height: 70,
          width: "75%",
          alignSelf: "center",
        }}
        resizeMode="contain"
        source={require("../../assets/DWText.png")}
      />
    </View>
  );
};
//======================================================
const context = createContext();
//======================================================
const Spinner = memo(({ text }) => {
  return (
    <View
      style={{
        textAlign: "center",
        marginBottom: web ? 20 : 5,
        marginTop: web ? 30 : 5,
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "flex-end",
      }}
    >
      <ArrowSpinner isSpinner size={web ? 28 : 19} style={{ maxWidth: 40 }} />
      <CustomText style={{ fontSize: web ? 18 : 13 }}>
        {text || " Loading..."}
      </CustomText>
    </View>
  );
});
const SmallSpinner = memo(() => {
  return (
    <View
      style={{
        textAlign: "center",
        flexDirection: "row",
        justifyContent: "center",
      }}
    >
      <ArrowSpinner size={15} style={{ flex: web ? "unset" : 1, margin: 0 }} />
      <CustomText> Loading...</CustomText>
    </View>
  );
});
//======================================================
const AccordionItem = ({
  children,
  title,
  isExpand = false,
  isAccordion = true,
  style = {},
  titleStyle = {},
}) => {
  const [expanded, setExpanded] = useState(isExpand);
  function toggleItem() {
    setExpanded(!expanded);
  }
  return (
    <View style={[styles.accordContainer, style]}>
      <TouchableOpacity
        activeOpacity={1}
        style={styles.accordHeader}
        onPress={isAccordion ? toggleItem : () => {}}
      >
        <CustomText
          style={[
            styles.accordTitle,
            { alignItems: "center", display: "flex" },
            titleStyle,
          ]}
          bold={true}
        >
          {isAccordion && (
            <Entypo
              name={expanded ? "chevron-up" : "chevron-down"}
              size={25}
              style={{ marginRight: 10 }}
            />
          )}

          {title}
        </CustomText>
      </TouchableOpacity>
      {expanded && <View style={styles.accordBody}>{children}</View>}
      <Separator />
    </View>
  );
};

const AdditionalPage = ({
  handleBack,
  title,
  children,
  rightContent = <CustomText> </CustomText>,
  isClose = false,
  isNeedBack = true,
  titleFontSize = 22,
  form = null,
}) => {
  const keyboardHeight = useKeyboardHeight() || 0;

  const bodyCon = (
    <>
      <View
        style={
          !web ? { flexDirection: "row", justifyContent: "space-between" } : {}
        }
      >
        <View>
          {isNeedBack && !web && (
            <TouchableOpacity activeOpacity={1} onPress={handleBack}>
              <CustomText
                bold={true}
                style={{ paddingVertical: 10, paddingHorizontal: 10 }}
              >
                {isClose ? (
                  <>
                    <FontAwesome size={16} name="close" color="red" /> Close
                  </>
                ) : (
                  <>
                    <FontAwesome name="angle-left" size={16} /> Back
                  </>
                )}
              </CustomText>
            </TouchableOpacity>
          )}
        </View>
        <View style={{ alignItems: "center", marginTop: 5 }}>
          <CustomText style={{ fontSize: titleFontSize || 22 }} bold={true}>
            {title}
          </CustomText>
        </View>
        <View style={{ paddingVertical: 10, paddingHorizontal: 10 }}>
          {rightContent}
        </View>
      </View>
      {children}
    </>
  );

  return (
    <View
      style={[
        {
          backgroundColor: "white",
          flex: 1,
          width: "100%",
        },
        form === "calculator"
          ? {}
          : {
              maxHeight:
                Dimensions.get("window").height - (150 + keyboardHeight),
            },
      ]}
    >
      {form === "calculator" ? (
        bodyCon
      ) : (
        <ScrollContainer>{bodyCon}</ScrollContainer>
      )}
    </View>
  );
};

const DrawerView = (props) => {
  const {
      isShow = false,
      body = <></>,
      onClose = () => {},
      bodyStyle = {},
      bodyName = null,
      From = "",
      isSmallScreen = false,
    } = props,
    keyboardHeight = useKeyboardHeight() || 0,
    isMobileWeb = Dimensions.get("window").width <= 480; //|| !isSmallScreen;

  return (
    <Modal visible={isShow} transparent animationType="slide">
      <TouchableOpacity
        activeOpacity={1}
        style={[
          {
            height: "100%",
            flex: 1,
            backgroundColor: "#535252a3",
          },
          web
            ? {
                width: From == "DashBoard" ? 750 : isMobileWeb ? "100%" : 422,
                left: isMobileWeb ? 0 : 369 - (From == "DashBoard" ? 15 : 0),
                bottom: 0,
                position: "absolute",
                height: "calc(100% - 86px)",
                // ...(!isSmallScreen ? { justifyContent: "center" } : {}),
              }
            : {},
        ]}
        onPress={() => onClose()}
      ></TouchableOpacity>
      <View
        style={[
          {
            backgroundColor: "#fff",
            zIndex: 2,
            width: "100%",
            borderColor: "#d3dadf",
            borderWidth: 2,
            borderRadius: 5,
            boxShadow: "0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)",
            alignSelf: "flex-end",
            justifyContent: !web ? "flex-start" : "flex-end",
            position: "absolute",
          },

          ["Signup", "Login", "DocumentUpload", "ChangePassword"].includes(
            bodyName
          )
            ? {
                marginBottom: keyboardHeight,
                bottom: keyboardHeight * -1,
              }
            : From == "pickFileOption"
            ? {
                bottom: 0,
              }
            : bodyName == "android"
            ? {
                // bottom: keyboardHeight * -1,
                marginTop: 150,
              }
            : {
                bottom: keyboardHeight,
              },
        ]}
      >
        <View
          style={
            web
              ? {
                  width: From == "DashBoard" ? 750 : isMobileWeb ? "100%" : 419,
                  position: "absolute",
                  backgroundColor: "#fff",
                  left: isMobileWeb ? 0 : 369 - (From == "DashBoard" ? 15 : 0),
                }
              : {}
          }
        >
          <TouchableOpacity
            style={
              From == "StartScreen"
                ? {
                    alignItems: "flex-end",
                    backgroundColor: "#204C86",
                    paddingVertical: 8,
                    paddingHorizontal: 5,
                    borderRadius: 3,
                  }
                : {
                    alignItems: "center",
                    backgroundColor: "#f5f5f5",
                    paddingVertical: 0,
                  }
            }
            activeOpacity={1}
            onPress={onClose}
          >
            {From == "StartScreen" ? (
              <>
                <EvilIcons
                  name={"close"}
                  size={30}
                  style={{
                    color: "#FFFFFF",
                  }}
                />
              </>
            ) : (
              <>
                <EvilIcons
                  name={"chevron-down"}
                  size={45}
                  style={{
                    color: "#000000",
                    marginBottom: 0,
                  }}
                />
              </>
            )}
          </TouchableOpacity>
          <View
            style={[
              {
                paddingHorizontal: !web ? 20 : 0,
                paddingVertical: 10,
                borderBottomWidth: 1.5,
                borderBottomColor: "#f5f5f5",
              },
              bodyStyle,
            ]}
          >
            {body}
          </View>
        </View>
      </View>
    </Modal>
  );
};
const FloatPage = (props) => {
  const {
    children,
    setFloatPage = () => {},
    needHeaderContent = false,
    preventClose = false,
    style = null,
  } = props;

  const slideAnimation = useRef(new Animated.Value(3000)).current;
  const isMobileWeb = Dimensions.get("window").width <= 480;
  useEffect(() => {
    Animated.timing(slideAnimation, {
      toValue: 0,
      duration: 500,
      useNativeDriver: false,
    }).start();
  }, [slideAnimation]);

  const handleClose = () => {
    Animated.timing(slideAnimation, {
      toValue: 3000,
      duration: 500,
      useNativeDriver: false,
    }).start();
    setTimeout(() => {
      setFloatPage(null);
    }, 500);
  };
  const handleCloseDrawer = () => {
    if (!preventClose) {
      handleClose();
    } else {
      setFloatPage();
    }
  };

  return (
    <>
      {children ? (
        <Animated.View
          style={{
            position: "fixed",
            height: "100%",
            width: "100%",
            backgroundColor: "#8787874f",
            zIndex: 11,
            transform: [{ translateX: slideAnimation }],
          }}
        >
          <TouchableOpacity
            activeOpacity={1}
            onPress={handleCloseDrawer}
            style={{
              height: "100%",
              width: "calc(100% -750px)",
            }}
          ></TouchableOpacity>
          <View
            style={[
              {
                right: 0,
                position: "absolute",
                height: "100%",
                width: isMobileWeb ? "100%" : 750,
                backgroundColor: "#fff",
                zIndex: 11,
              },
              style ? style : {},
            ]}
          >
            {needHeaderContent && (
              <View style={[{ backgroundColor: "white", width: "100%" }]}>
                <View style={{ position: "absolute", zIndex: 1 }}>
                  <TouchableOpacity
                    activeOpacity={0.5}
                    onPress={handleCloseDrawer}
                  >
                    <CustomText
                      bold={true}
                      style={[
                        { paddingVertical: 10, paddingHorizontal: 10 },
                        web ? { cursor: "pointer" } : {},
                      ]}
                    >
                      <FontAwesome size={16} name="close" color="red" /> Close
                    </CustomText>
                  </TouchableOpacity>
                </View>

                <Separator />
              </View>
            )}
            {children}
          </View>
        </Animated.View>
      ) : (
        <></>
      )}
    </>
  );
};

const AddRemoveButton = ({ onPress, text, isAdd = false }) => {
  return (
    <>
      {web ? (
        <div
          tabIndex={0}
          onClick={onPress}
          style={{
            backgroundColor: isAdd ? "#ffb752" : "#d43f3a",
            borderRadius: 25,
            padding: "10px 20px",
            marginBottom: 8,
            textAlign: "center",
            cursor: "pointer",
          }}
          onKeyDown={(event) => {
            let { keyCode } = event;
            if (keyCode === 32 || keyCode === 13) {
              event.preventDefault();
              onPress();
            }
          }}
        >
          <CustomText
            style={[
              {
                color: "#fff",
                fontSize: web ? 18 : 16,
                alignSelf: "center",
                width: "max-content",
              },
            ]}
          >
            {text}
          </CustomText>
        </div>
      ) : (
        <TouchableOpacity
          onPress={onPress}
          activeOpacity={1}
          style={[
            {
              backgroundColor: isAdd ? "#ffb752" : "#d43f3a",
              borderRadius: 25,
              paddingVertical: 6,
              marginBottom: 8,
            },
          ]}
        >
          <CustomText
            style={[
              {
                color: "#fff",
                fontSize: 17,
                alignSelf: "center",
              },
            ]}
          >
            {text}
          </CustomText>
        </TouchableOpacity>
      )}
    </>
  );
};
const PageBottom = () => {
  return <CustomText style={styles.pageFooter}>Page Bottom</CustomText>;
};

const styles = StyleSheet.create({
  spinnerContainer: {
    flex: 1,
    justifyContent: "center",
    textAlign: "center",
    paddingTop: 30,
    // backgroundColor: "#ecf0f1",
    padding: 8,
    height: "100%",
  },
  spinnerTextStyle: {
    // color: "green",
    top: "49%",
    left: "52%",
    position: "absolute",
  },
  separator: {
    marginVertical: 12,
  },
  title: {
    fontSize: web ? 24 : 21,
    marginBottom: web ? 25 : 15,
    color: "#0f203a",
  },
  loanId: {
    marginBottom: 10,
    color: "#0f203a",
    flexDirection: "row",
    alignSelf: "flex-end",
  },
  ShowLoanId: {
    marginBottom: 10,
    color: "#0f203a",
    flexDirection: "row",
    alignSelf: "flex-end",
    display: "flex",
  },
  HideLoanId: {
    marginBottom: 10,
    color: "#0f203a",
    flexDirection: "row",
    alignSelf: "flex-end",
    display: "none",
  },
  buttonContainer: {
    backgroundColor: "#295b9a",

    paddingHorizontal: 20,
    borderRadius: 5,
    paddingVertical: 6,
    marginLeft: 8,
    height: 35,
    justifyContent: "center",
  },
  buttonText: {
    fontSize: 20,
    color: "#fff",
    alignSelf: "center",
  },
  optionContainer: {
    // backgroundColor: "#f2f5f8",
    borderRadius: 20,
    paddingVertical: web ? 6 : 5,
    paddingHorizontal: 12,
    borderColor: "#043888",
    borderWidth: 2,
    marginBottom: web ? 25 : 20,
    flexDirection: "row",
  },
  optionText: {
    fontSize: web ? 22 : 18,
    color: "#043888",
    width: "100%",
    textAlign: "center",
    flex: 1,
    flexShrink: 1,
  },
  inputBox: {
    ...{
      borderWidth: 0,
      // outlineWidth: 0,
      borderRadius: 5,
      fontSize: web ? 15 : 16,
      backgroundColor: "rgba(0,0,0,.04)",
      color: "#51575d",
      width: "100%",
      padding: 5,
      height: 34,
    },
    // ...(web && {
    //   ":focus": { "outline-width": 0, borderWidth: 0 },
    //   ":focus-visible": { "outline-width": 0, borderWidth: 0 },
    // }),
  },
  inputBoxLabel: {
    // display: "inline",
    position: "absolute",
    backgroundColor: "#fff",
    top: -12,
    left: 3,
    fontSize: 14,
    color: "gray",
    paddingHorizontal: 3,
    flex: 1,
  },
  inputBoxContainer: {
    flexDirection: "row",
    borderWidth: 1,
    borderRadius: 8,
    padding: 8,
    marginBottom: 20,
    minHeight: 50,
  },
  iconButtonContainer: {
    backgroundColor: "#508bc9",
    borderColor: "#508bc9",
    paddingVertical: web ? 10 : 8,
    paddingHorizontal: web ? 20 : 15,
    borderWidth: 3,
    alignSelf: "flex-start",
    borderRadius: 5,
    marginTop: 3,
  },

  iconButtonHovered: {
    borderColor: "#295B9A",
    //background: "#3872AF",
    backgroundColor: "#3872AF",
  },

  iconButtonHovered1: {
    borderColor: "#3872AF",
    //background: "#3872AF",
    backgroundColor: "#F9F9F9",
  },
  iconButtonText: {
    fontSize: 18,
    color: "#fff",
    fontWeight: "700",
  },
  accordContainer: {
    paddingBottom: 4,
  },
  accordHeader: {
    padding: 8,
    backgroundColor: "#f5f5f5",
    borderColor: "#ddd",
    borderWidth: 1,
    borderRadius: 4,
    borderBottomRightRadius: 0,
    borderBottomLeftRadius: 0,
    flex: 1,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  accordTitle: {
    fontSize: 16,
  },
  accordBody: {
    padding: 12,
    borderColor: "#ddd",
    borderWidth: 1,
    borderTopWidth: 0,
    marginTop: -2,
  },
  pageFooter: {
    paddingLeft: 20,
    padding: 5,
    backgroundColor: "rgb(48, 126, 204)",
    textAlign: "center",
    color: "rgb(255,255, 255)",
    marginBottom: 10,
    minWidth: "100%",
  },
});

export {
  Separator,
  Option,
  Button,
  InputBox,
  Title,
  LoanId,
  IconButton,
  WebPageView,
  context,
  DMCImage,
  Spinner,
  SmallSpinner,
  AccordionItem,
  AdditionalPage,
  ModalButton,
  DrawerView,
  FloatPage,
  AddRemoveButton,
  PageBottom,
};
